import React from 'react'
import { t } from 'i18next'
import StandardQualityIcon from '@/scenes/Editor/components/Icons/StandardQualityIcon'
import HDQualityIcon from '@/scenes/Editor/components/Icons/HDQualityIcon'
import FourKQualityIcon from '@/scenes/Editor/components/Icons/FourKQualityIcon'
import SuperIcon from '@/scenes/Editor/components/Icons/SuperIcon'
import type { SuggestionButtonPopover } from '@/scenes/Editor/components/EditWithAI/components/suggestedButtonSets'
import type { ImageQuality } from '@/store/slices/aiFashion/types'

export type { ImageQuality }

type QualityOption = {
  value: ImageQuality
  label: string
  icon: JSX.Element
}

/** The quality tiers: standard, HD, 4K. */
function getQualityOptions(): QualityOption[] {
  return [
    { value: 'standard', label: t('Standard quality'), icon: <StandardQualityIcon size={24} /> },
    { value: 'hd', label: t('High quality'), icon: <HDQualityIcon size={24} /> },
    { value: '4k', label: t('4K quality'), icon: <FourKQualityIcon size={24} /> },
  ]
}

/** The tier a trigger should display — its icon and label mirror the selection. */
export function getSelectedQualityOption(quality: ImageQuality): QualityOption {
  const options = getQualityOptions()
  return options.find((option) => option.value === quality) ?? options[0]
}

type QualityListPopoverParams = {
  title?: string
  quality: ImageQuality
  onSelect: (quality: ImageQuality) => void
  /** Super-plan access. Locked tiers keep a SuperIcon instead of the check. */
  isHDAvailable?: boolean
}

export function createQualityListPopover({
  title,
  quality,
  onSelect,
  isHDAvailable = false,
}: QualityListPopoverParams): SuggestionButtonPopover {
  return {
    designType: 'list_white',
    title: title ?? t('Quality'),
    width: 262,
    items: getQualityOptions().map((option) => ({
      icon: option.icon,
      txt: option.label,
      value: option.value,
      // A locked tier still reads as locked once selected — only unselected
      // locked tiers show the upsell badge, matching the original button.
      rightElement:
        option.value !== 'standard' && !isHDAvailable && quality !== option.value
          ? <SuperIcon />
          : undefined,
      selected: quality === option.value,
      onClick: () => onSelect(option.value),
      closePopoverOnClick: true,
    })),
  }
}
